if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Setas de navegação: independentes do slider nativo (ver setupThumbnailArrows)
        this.setupThumbnailArrows();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, skipThumbnailScroll = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, skipThumbnailScroll);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // As setas passam SEMPRE para a imagem anterior/seguinte e dão a volta no fim/começo.
      // Não dependem do slider nativo (que desabilitava a seta quando a última miniatura já
      // estava visível, ou seja, quando "não havia imagem escondida para rolar").
      setupThumbnailArrows() {
        const thumbnails = this.elements.thumbnails;
        const buttons = thumbnails.querySelectorAll('.slider-button');

        // Impede o slider nativo de manter as setas desabilitadas (botão disabled não recebe clique).
        thumbnails.enableSliderLooping = true;
        buttons.forEach((button) => {
          const enable = () => {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
          };
          enable();
          new MutationObserver(() => {
            if (button.hasAttribute('disabled') || button.hasAttribute('aria-disabled')) enable();
          }).observe(button, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        });

        // Captura o clique antes do handler nativo (que só rola a faixa de miniaturas) e assume o controle.
        thumbnails.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('.slider-button');
            if (!button || !thumbnails.contains(button)) return;
            event.preventDefault();
            event.stopPropagation();
            this.onThumbnailArrowClick(button);
          },
          true
        );
      }

      getVisibleThumbnailItems() {
        // Recalculado a cada clique; ignora miniaturas escondidas (ex.: imagens de outras variantes).
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (item) => item.getClientRects().length > 0
        );
      }

      onThumbnailArrowClick(button) {
        const direction = button.name === 'next' ? 1 : -1;
        const items = this.getVisibleThumbnailItems();
        if (items.length < 2) return;

        let currentIndex = items.findIndex((item) => item.querySelector('button').hasAttribute('aria-current'));
        if (currentIndex < 0) {
          const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
          const activeId = activeSlide ? activeSlide.dataset.mediaId : null;
          currentIndex = items.findIndex((item) => item.dataset.target === activeId);
        }
        if (currentIndex < 0) currentIndex = 0;

        // Volta ao começo depois da última e vai para a última antes da primeira.
        const nextIndex = (currentIndex + direction + items.length) % items.length;
        const nextItem = items[nextIndex];
        this.setActiveMedia(nextItem.dataset.target, false, true);
        this.scrollThumbnailIntoView(nextItem);
      }

      scrollThumbnailIntoView(item) {
        const strip = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        if (!strip) return;
        const stripRect = strip.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        if (itemRect.left >= stripRect.left && itemRect.right <= stripRect.right) return;
        const padding = parseFloat(window.getComputedStyle(strip).paddingLeft) || 0;
        strip.scrollTo({
          left: strip.scrollLeft + (itemRect.left - stripRect.left) - padding,
          behavior: 'smooth',
        });
      }

      setActiveThumbnail(thumbnail, skipScroll = false) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (skipScroll) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
